import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { MapPin, RefreshCw, Filter, X, Clock, Truck, Navigation } from "lucide-react";

function BusLocationTracking() {
  const navigate = useNavigate();
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [error, setError] = useState("");
  const [schools, setSchools] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({
    schoolName: "",
    vehicleName: ""
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    pageSize: 50
  });

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    // When school changes, reset vehicle filter and fetch vehicles for that school
    if (filters.schoolName && schools.length > 0) {
      const selectedSchool = schools.find(s => s.name === filters.schoolName);
      if (selectedSchool) {
        // Reset vehicle filter when school changes
        setFilters(prev => ({ ...prev, vehicleName: "" }));
        fetchVehiclesBySchool(selectedSchool._id).then((fetchedVehicles) => {
          // After vehicles are fetched, fetch tracking data
          if (fetchedVehicles.length > 0) {
            setPagination(prev => ({ ...prev, page: 1 }));
            // Fetch tracking data with current filters
            fetchTrackingDataForVehicles(fetchedVehicles);
          } else {
            setTrackingData([]);
            setPagination({ page: 1, total: 0, totalPages: 0, pageSize: 50 });
          }
        });
      }
    } else if (!filters.schoolName) {
      // If no school selected, clear vehicles and fetch all tracking data
      setVehicles([]);
      setFilters(prev => ({ ...prev, vehicleName: "" }));
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTrackingData();
    }
  }, [filters.schoolName, schools]);


  useEffect(() => {
    // Fetch tracking data when vehicle filter changes
    // Only fetch if school is not selected OR vehicles are loaded
    if (!filters.schoolName || (vehicles.length > 0 && !loadingVehicles)) {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTrackingData();
    }
  }, [filters.vehicleName]);

  useEffect(() => {
    // Fetch tracking data when pagination changes
    // Only fetch if school is not selected OR vehicles are loaded
    if (!filters.schoolName || (vehicles.length > 0 && !loadingVehicles)) {
      fetchTrackingData();
    }
  }, [pagination.page]);

  const fetchSchools = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.schools);
      setSchools(response.data.data?.schools || []);
    } catch (err) {
      console.error("Failed to fetch schools:", err);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.vehicles);
      setVehicles(response.data.data?.vehicles || []);
    } catch (err) {
      console.error("Failed to fetch vehicles:", err);
    }
  };

  const fetchVehiclesBySchool = async (schoolId) => {
    setLoadingVehicles(true);
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.vehiclesBySchool(schoolId));
      const fetchedVehicles = response.data.data?.vehicles || [];
      setVehicles(fetchedVehicles);
      return fetchedVehicles;
    } catch (err) {
      console.error("Failed to fetch vehicles by school:", err);
      setVehicles([]);
      return [];
    } finally {
      setLoadingVehicles(false);
    }
  };

  const fetchTrackingData = async () => {
    // If school is selected but vehicles are still loading, wait
    if (filters.schoolName && loadingVehicles) {
      return;
    }

    // If school is selected but no vehicles found, clear data
    if (filters.schoolName && vehicles.length === 0) {
      setTrackingData([]);
      setPagination({ page: 1, total: 0, totalPages: 0, pageSize: 50 });
      setLoading(false);
      return;
    }

    await fetchTrackingDataInternal();
  };

  const fetchTrackingDataForVehicles = async (fetchedVehicles) => {
    // This function is called when vehicles are freshly loaded
    // We know vehicles exist, so proceed directly
    await fetchTrackingDataInternal();
  };

  const fetchTrackingDataInternal = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.append("page", pagination.page);
      if (filters.schoolName) params.append("schoolName", filters.schoolName);
      if (filters.vehicleName) params.append("vehicleName", filters.vehicleName);
      const queryString = params.toString();
      const url = API_ENDPOINTS.busTrackingHistory(queryString || undefined);
      
      const response = await axiosInstance.get(url);
      const trackingData = response.data.data || [];
      
      setTrackingData(trackingData);
      if (response.data.total !== undefined) {
        setPagination({
          page: response.data.page || 1,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
          pageSize: response.data.pageSize || 50
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch tracking data");
      setTrackingData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (vehicleId) => {
    navigate(`/dashboard/bus-tracking/vehicle/${vehicleId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (durationString) => {
    if (!durationString || durationString === "N/A") return "N/A";
    return durationString;
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          Active
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        Inactive
      </span>
    );
  };

  const getVehicleTypeBadge = (type) => {
    const colors = type === "bus" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800";
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors}`}>
        {type ? type.toUpperCase() : "N/A"}
      </span>
    );
  };

  const openGoogleMaps = (latitude, longitude) => {
    if (latitude && longitude) {
      window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Bus Location Tracking</h2>
        <button
          onClick={fetchTrackingData}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md">

        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center mb-2">
            <Filter className="w-4 h-4 mr-2 text-gray-600" />
            <span className="font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
              <select
                value={filters.schoolName}
                onChange={(e) => setFilters({ ...filters, schoolName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">All Schools</option>
                {schools.map((school) => (
                  <option key={school._id} value={school.name}>
                    {school.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Name</label>
              <div className="relative">
                <select
                  value={filters.vehicleName}
                  onChange={(e) => setFilters({ ...filters, vehicleName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={!filters.schoolName || loadingVehicles}
                >
                  <option value="">
                    {loadingVehicles 
                      ? "Loading vehicles..." 
                      : filters.schoolName 
                        ? (vehicles.length > 0 ? "All Vehicles" : "No Vehicles Found") 
                        : "Select School First"}
                  </option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle._id} value={vehicle.vehicleNumber}>
                      {vehicle.vehicleNumber}
                    </option>
                  ))}
                </select>
                {loadingVehicles && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>
          {(filters.schoolName || filters.vehicleName) && (
            <button
              onClick={() => {
                setFilters({ schoolName: "", vehicleName: "" });
                setPagination({ page: 1, total: 0, totalPages: 0, pageSize: 50 });
              }}
              className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              <X className="w-4 h-4 mr-1" />
              Clear Filters
            </button>
          )}
        </div>

        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            {error}
          </div>
        )}

        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600" />
              <p className="mt-2 text-gray-600">Loading tracking data...</p>
            </div>
          ) : trackingData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trackingData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.vehicleNumber || "N/A"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {getVehicleTypeBadge(item.type)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.school?.name || "N/A"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {getStatusBadge(item.isCurrentlyActive !== undefined ? item.isCurrentlyActive : item.isActive)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {item.currentLocation ? (
                          <button
                            onClick={() => openGoogleMaps(item.currentLocation.latitude, item.currentLocation.longitude)}
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <MapPin className="w-4 h-4 mr-1" />
                            View Map
                          </button>
                        ) : (
                          <span className="text-gray-400">No location</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.locationSharingStartTime)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.locationSharingEndTime)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDuration(item.duration)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewDetails(item.vehicleId)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No tracking data found</div>
          )}
          
          {/* Pagination */}
          {trackingData.length > 0 && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default BusLocationTracking;
